import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { SearchX } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ProductCard } from "@/components/ProductCard";
import { SearchForm } from "@/components/SearchForm";
import { SiteHeader } from "@/components/SiteHeader";
import { isRateLimited } from "@/content/loader";
import { productsQuery } from "@/content/queries";
import { useThrottledValue } from "@/hooks/useThrottledValue";
import { fr } from "@/i18n/fr";

/**
 * Catalogue (`/catalogue?q=`): the products whose 3D guide is ready, searchable by name. Without a
 * query the first one opens the page as a featured tile.
 */
export default function Catalogue() {
	const { q = "" } = useSearch({ from: "/_auth/catalogue" });
	const navigate = useNavigate({ from: "/catalogue" });
	const [input, setInput] = useState(q);
	const debounced = useThrottledValue(input.trim());
	// the URL keeps the search (shareable, back button); the input keeps what is being typed
	const urlQuery = useRef(q);
	urlQuery.current = q;
	useEffect(() => {
		if (debounced !== urlQuery.current)
			navigate({ search: { q: debounced || undefined }, replace: true });
	}, [debounced, navigate]);

	const { data, error, isPending, isPlaceholderData } = useQuery(
		productsQuery(q, true),
	);

	useEffect(() => {
		document.title = `${fr.catalog} · ${fr.appTitle}`;
	}, []);

	const items = data?.items ?? [];
	const featured = !q && !isPlaceholderData ? items[0] : undefined;
	const grid = featured ? items.filter((p) => p !== featured) : items;

	return (
		<div className="page">
			<SiteHeader />
			<div className="mx-auto max-w-6xl px-4 md:px-6">
				<div className="grid gap-5 py-8 md:grid-cols-[1fr_400px] md:items-end md:gap-8 md:py-12">
					<div>
						<p className="text-eyebrow text-fg-muted uppercase">{fr.catalog}</p>
						<h1 className="mt-2 font-display text-[36px] text-balance leading-10 tracking-tight md:text-[52px] md:leading-[56px]">
							{fr.catalogTitle}
						</h1>
						<p className="mt-3 max-w-md text-fg-muted">{fr.catalogIntro}</p>
					</div>
					<SearchForm initial={q} onChange={setInput} />
				</div>

				<main className="border-line border-t py-6 md:py-8">
					{error ? (
						<p
							role="alert"
							className="rounded-md bg-danger-soft px-3 py-2 text-[13px] text-danger leading-[18px]"
						>
							{isRateLimited(error) ? (
								fr.searchLimited
							) : (
								<>
									<b>{fr.catalogError}</b> — {error.message}
								</>
							)}
						</p>
					) : isPending ? (
						<p className="text-fg-muted">{fr.loading}…</p>
					) : items.length === 0 ? (
						<div className="grid justify-items-center gap-2 px-4 py-16 text-center text-fg-muted">
							<SearchX
								className="size-8"
								strokeWidth={1.25}
								aria-hidden="true"
							/>
							<p>{fr.noResults}</p>
						</div>
					) : (
						<div
							className={`grid gap-10 transition-opacity ${isPlaceholderData ? "opacity-60" : ""}`}
						>
							<p
								className="text-eyebrow text-fg-muted uppercase"
								aria-live="polite"
							>
								{fr.resultCount(data.total)}
							</p>
							{featured && <ProductCard product={featured} featured />}
							<ul className="grid list-none grid-cols-2 gap-x-4 gap-y-8 p-0 md:grid-cols-3 md:gap-x-6 md:gap-y-10 lg:grid-cols-4">
								{grid.map((product) => (
									<li key={product.slug}>
										<ProductCard product={product} />
									</li>
								))}
							</ul>
						</div>
					)}
				</main>
			</div>
		</div>
	);
}
